/**
 * Media pricing calculator — value framing for advertisers (e.g. DraftKings, FanDuel)
 * and Watchly's own deployment unit economics for a chain like Buffalo Wild Wings.
 *
 * All math is client-side. Source figures below are baked in (not user-adjustable);
 * everything in `MediaCalculatorInputs` is driven by the sliders in the UI.
 */

/** Industry / chain source data — fixed, cited inline for sales credibility. */
export const MEDIA_SOURCE_DATA = {
    /** Cost of a single national Monday Night Football spot (Ad Age, 2024–25 upfront). */
    nationalMnfAdCostPerSpot: 637_718,
    /** National MNF viewership (Nielsen, 2025). */
    nationalMnfViewers: 15_800_000,
    /** Buffalo Wild Wings total US locations. */
    bwwTotalLocations: 1_178,
    /** Viewers per BWW location on a MNF night. */
    viewersPerLocationMnf: 75,
    /** In-venue attention premium applied to the national CPM when pricing a Watchly spot. */
    inVenuePremiumMultiple: 2.5,
} as const;

/** Derived national CPM (cost per thousand impressions) ≈ $40.38. */
export const NATIONAL_MNF_CPM =
    (MEDIA_SOURCE_DATA.nationalMnfAdCostPerSpot / MEDIA_SOURCE_DATA.nationalMnfViewers) * 1000;

/** Cumulative cash-flow horizon for the operator chart. */
export const CASH_FLOW_HORIZON_MONTHS = 60;

/** All slider-adjustable inputs. */
export type MediaCalculatorInputs = {
    /** BWW locations deployed with Watchly hardware. */
    locationsDeployed: number;
    screensPerLocation: number;
    hardwareCostPerScreen: number;
    installCostPerLocation: number;
    /** SaaS fee charged to BWW, per location per month. */
    saasFeePerLocationPerMonth: number;
    /** Ad spots sold per MNF game across the deployed network. */
    adSpotsSoldPerGame: number;
    /** Price Watchly charges per network spot. */
    spotPrice: number;
    /** Watchly's commission on media revenue (0–1). */
    commissionFraction: number;
    gamesPerSeason: number;
};

export const DEFAULT_MEDIA_CALCULATOR_INPUTS: MediaCalculatorInputs = {
    locationsDeployed: MEDIA_SOURCE_DATA.bwwTotalLocations,
    screensPerLocation: 6,
    hardwareCostPerScreen: 1_000,
    installCostPerLocation: 500,
    saasFeePerLocationPerMonth: 200,
    adSpotsSoldPerGame: 5,
    spotPrice: 8_919,
    commissionFraction: 0.15,
    gamesPerSeason: 17,
};

export type CashFlowPoint = { month: number; cumulative: number };

export type MediaCalculatorOutputs = {
    /** Deployed BWW audience reached on a MNF night (locations × viewers/location). */
    bwwAudience: number;

    // --- Advertiser view ---
    /** What it would cost on national TV to reach the same BWW audience. */
    impliedTvCostPerSpot: number;
    /** CPM-derived price suggestion (national CPM × in-venue premium × audience). */
    suggestedSpotPrice: number;
    /** Effective CPM the advertiser pays through Watchly at the current spot price. */
    watchlyEffectiveCpm: number;
    /** Spot price − implied TV cost (the premium Watchly captures). */
    premiumOverImpliedTv: number;
    /** spotPrice / impliedTvCostPerSpot, e.g. 2.5× the implied TV cost. */
    premiumMultiple: number;

    // --- Operator / internal view ---
    upfrontDeploymentCost: number;
    annualSaasRevenue: number;
    /** Gross media revenue across a full MNF season (before commission). */
    seasonMediaRevenue: number;
    annualMediaCommissionRevenue: number;
    totalAnnualRevenue: number;
    monthlyRevenue: number;
    /** Months until cumulative revenue covers upfront cost; null if revenue is zero. */
    breakEvenMonths: number | null;
    cashFlow: CashFlowPoint[];
};

export function computeMediaCalculator(inputs: MediaCalculatorInputs): MediaCalculatorOutputs {
    const {
        locationsDeployed,
        screensPerLocation,
        hardwareCostPerScreen,
        installCostPerLocation,
        saasFeePerLocationPerMonth,
        adSpotsSoldPerGame,
        spotPrice,
        commissionFraction,
        gamesPerSeason,
    } = inputs;

    const bwwAudience = locationsDeployed * MEDIA_SOURCE_DATA.viewersPerLocationMnf;

    // Advertiser framing
    const impliedTvCostPerSpot =
        MEDIA_SOURCE_DATA.nationalMnfAdCostPerSpot * (bwwAudience / MEDIA_SOURCE_DATA.nationalMnfViewers);
    const suggestedSpotPrice =
        NATIONAL_MNF_CPM * MEDIA_SOURCE_DATA.inVenuePremiumMultiple * (bwwAudience / 1000);
    const watchlyEffectiveCpm = bwwAudience > 0 ? (spotPrice / bwwAudience) * 1000 : 0;
    const premiumOverImpliedTv = spotPrice - impliedTvCostPerSpot;
    const premiumMultiple = impliedTvCostPerSpot > 0 ? spotPrice / impliedTvCostPerSpot : 0;

    // Operator unit economics
    const upfrontDeploymentCost =
        locationsDeployed * (screensPerLocation * hardwareCostPerScreen + installCostPerLocation);
    const annualSaasRevenue = locationsDeployed * saasFeePerLocationPerMonth * 12;
    const seasonMediaRevenue = adSpotsSoldPerGame * spotPrice * gamesPerSeason;
    const annualMediaCommissionRevenue = seasonMediaRevenue * commissionFraction;
    const totalAnnualRevenue = annualSaasRevenue + annualMediaCommissionRevenue;
    const monthlyRevenue = totalAnnualRevenue / 12;

    // Cumulative cash flow: month 0 starts at -upfront, then accrues monthly-averaged revenue.
    const cashFlow: CashFlowPoint[] = [];
    for (let month = 0; month <= CASH_FLOW_HORIZON_MONTHS; month++) {
        cashFlow.push({ month, cumulative: -upfrontDeploymentCost + monthlyRevenue * month });
    }

    const breakEvenMonths =
        monthlyRevenue > 0 ? Math.ceil(upfrontDeploymentCost / monthlyRevenue) : null;

    return {
        bwwAudience,
        impliedTvCostPerSpot,
        suggestedSpotPrice,
        watchlyEffectiveCpm,
        premiumOverImpliedTv,
        premiumMultiple,
        upfrontDeploymentCost,
        annualSaasRevenue,
        seasonMediaRevenue,
        annualMediaCommissionRevenue,
        totalAnnualRevenue,
        monthlyRevenue,
        breakEvenMonths,
        cashFlow,
    };
}
